/**
 * 🔒 VAULT-SEAL-ADJACENT — DO NOT MODIFY (Feb 24 2026). See /app/memory/VAULT_SEAL.md.
 * Sealed until the user types "Upgrade Vault".
 *
 * Polls /api/admin/vault/requests every 30s. On any increase in pending_count
 * vs last poll, fires a Sonner toast and plays a short two-tone chime via the
 * Web Audio API (no asset file needed).
 *
 * Mount this component once inside AppShell when role === "admin".
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const POLL_MS = 30 * 1000;
const STORAGE_KEY = 'sl_admin_vault_pending_count';

const playChime = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const tones = [
      { freq: 880, start: 0.0, dur: 0.18 },
      { freq: 1175, start: 0.16, dur: 0.22 },
    ];
    tones.forEach(({ freq, start, dur }) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = 'sine';
      o.frequency.setValueAtTime(freq, now + start);
      g.gain.setValueAtTime(0.0001, now + start);
      g.gain.exponentialRampToValueAtTime(0.18, now + start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
      o.connect(g).connect(ctx.destination);
      o.start(now + start);
      o.stop(now + start + dur + 0.02);
    });
    setTimeout(() => ctx.close().catch(() => {}), 800);
  } catch {
    /* noop — silent if browser blocks autoplay */
  }
};

const AdminVaultNotifier = () => {
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const lastCountRef = useRef(
    Number(localStorage.getItem(STORAGE_KEY) || 0),
  );
  const firstPollRef = useRef(true);

  useEffect(() => {
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      try {
        const res = await authFetch(`${BACKEND_URL}/api/admin/vault/requests`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const cur = data.pending_count || 0;
        const prev = lastCountRef.current;
        if (!firstPollRef.current && cur > prev) {
          const delta = cur - prev;
          playChime();
          toast(
            `${delta} new vault request${delta === 1 ? '' : 's'}`,
            {
              description: `${cur} pending · click to review`,
              icon: <Lock className="w-4 h-4 text-fuchsia-300" />,
              action: {
                label: 'Review',
                onClick: () => navigate('/admin?tab=vault'),
              },
              duration: 8000,
            },
          );
        }
        lastCountRef.current = cur;
        localStorage.setItem(STORAGE_KEY, String(cur));
        firstPollRef.current = false;
      } catch {
        /* network errors are silently ignored */
      }
    };

    poll(); // immediate
    timer = setInterval(poll, POLL_MS);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [BACKEND_URL, navigate]);

  return null;
};

export default AdminVaultNotifier;
